// src/components/admin/AdminPlans.js
import React, { useState, useEffect, useRef } from 'react';
import {
  getAllTasks, createTask,
  getWeeklySchedule, saveWeeklySchedule,
  getAllDailyPlans, saveDailyPlan,
  deleteDailyPlansForDayWeek,
  getAllCampPlans, saveCampPlan
} from '../../services/firebase/firestoreService';
import { db } from '../../services/firebase/config';
import { doc, updateDoc } from 'firebase/firestore';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday'];
const LEVELS = [
  { value: 'beginnerBoys', label: 'Beginner Boys', short: 'BB' },
  { value: 'beginnerGirls', label: 'Beginner Girls', short: 'BG' },
  { value: 'advancedBoys', label: 'Advanced Boys', short: 'AB' },
  { value: 'advancedGirls', label: 'Advanced Girls', short: 'AG' },
  { value: 'specialBoys', label: 'Special Boys', short: 'SB' },
  { value: 'specialGirls', label: 'Special Girls', short: 'SG' },
];
const DAY_MAP = { monday:'Mon',tuesday:'Tue',wednesday:'Wed',thursday:'Thu',friday:'Fri',saturday:'Sat' };
const CAT_MAP = { beginnerBoys:'BB',beginnerGirls:'BG',advancedBoys:'AB',advancedGirls:'AG',specialBoys:'SB',specialGirls:'SG' };
const TYPE_COLORS = { athletics:'#2563eb', strength:'#28a745', nutrition:'#fd7e14' };

export default function AdminPlans() {
  const [section, setSection] = useState('tasks');
  const [tasks, setTasks] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [plans, setPlans] = useState([]);
  const [campPlans, setCampPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  // Task form
  const [newTask, setNewTask] = useState({
    nameEn:'', nameMr:'', type:'athletics', hasMediaPipe:false, mediaPipeType:'',
    youtubeURL:'', description:''
  });
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({});

  // Schedule
  const [selectedDay, setSelectedDay] = useState('monday');
  const dragItem = useRef(null);
  const dragOver = useRef(null);
  const dragSource = useRef(null);

  // Plan grid
  const [planDay, setPlanDay] = useState('monday');
  const [planWeek, setPlanWeek] = useState(1);
  const [gridData, setGridData] = useState({});

  // Camp plans
  const [selectedCamp, setSelectedCamp] = useState(1);
  const [campTaskList, setCampTaskList] = useState([]); // ordered array of taskIds included in camp
  const [campGrid, setCampGrid] = useState({});
  const campDragItem = useRef(null);
  const campDragOver = useRef(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, s, p, c] = await Promise.all([
        getAllTasks(), getWeeklySchedule(),
        getAllDailyPlans(), getAllCampPlans()
      ]);
      setTasks(t || []);
      setSchedule(s || {});
      setPlans(p || []);
      setCampPlans(c || []);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (text, type = 'success') => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg(null), 4000);
  };

  // ===== TASK LIBRARY =====
  const isTaskInUse = (taskId) => {
    const inPlans = plans.some(p => p.tasks?.some(t => t.taskId === taskId));
    const inSchedule = Object.values(schedule).some(day => day?.includes(taskId));
    const inCamps = campPlans.some(c => c.tasks?.some(t => t.taskId === taskId));
    return inPlans || inSchedule || inCamps;
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.nameEn || !newTask.nameMr) {
      showMsg('Both English and Marathi names required', 'error'); return;
    }
    setLoading(true);
    try {
      await createTask({
        name: { en: newTask.nameEn, mr: newTask.nameMr },
        type: newTask.type,
        hasMediaPipe: newTask.hasMediaPipe,
        mediaPipeType: newTask.hasMediaPipe ? newTask.mediaPipeType : null,
        youtubeURL: newTask.youtubeURL.trim() || '',
        description: newTask.description.trim() || ''
      });
      showMsg('Task created');
      setNewTask({
        nameEn:'', nameMr:'', type:'athletics', hasMediaPipe:false, mediaPipeType:'',
        youtubeURL:'', description:''
      });
      loadAll();
    } catch (e) {
      showMsg('Error creating task: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = async (taskId) => {
    if (!window.confirm('Save changes to this task?')) return;
    try {
      await updateDoc(doc(db, 'fitnessTasks', taskId), {
        name: { en: editTaskForm.nameEn, mr: editTaskForm.nameMr },
        type: editTaskForm.type,
        hasMediaPipe: editTaskForm.hasMediaPipe,
        mediaPipeType: editTaskForm.hasMediaPipe ? editTaskForm.mediaPipeType : null,
        youtubeURL: (editTaskForm.youtubeURL || '').trim(),
        description: (editTaskForm.description || '').trim()
      });
      showMsg('Task updated');
      setEditingTaskId(null);
      loadAll();
    } catch (e) {
      showMsg('Error updating task', 'error');
    }
  };

  const handleDeleteTask = async (task) => {
    if (isTaskInUse(task.id)) {
      showMsg(`Cannot delete "${task.name?.en}" — it is used in existing plans or schedule`, 'error');
      return;
    }
    if (!window.confirm(`Delete task "${task.name?.en}"? This cannot be undone.`)) return;
    try {
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(db, 'fitnessTasks', task.id));
      showMsg('Task deleted');
      loadAll();
    } catch (e) {
      showMsg('Error deleting task', 'error');
    }
  };

  // ===== WEEKLY SCHEDULE — DRAG AND DROP =====
  const dayTasks = schedule[selectedDay] || [];
  const availableTasks = tasks.filter(t => !dayTasks.includes(t.id));

  const handleDragStart = (e, taskId, source) => {
    dragItem.current = taskId;
    dragSource.current = source;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverAssigned = (e, taskId) => {
    e.preventDefault();
    dragOver.current = taskId;
  };

  const handleDropOnAssigned = (e) => {
    e.preventDefault();
    const taskId = dragItem.current;
    const source = dragSource.current;
    let newDayTasks = [...dayTasks];

    if (source === 'available') {
      if (!newDayTasks.includes(taskId)) newDayTasks.push(taskId);
    } else if (source === 'assigned' && dragOver.current && dragOver.current !== taskId) {
      const fromIndex = newDayTasks.indexOf(taskId);
      const toIndex = newDayTasks.indexOf(dragOver.current);
      newDayTasks.splice(fromIndex, 1);
      newDayTasks.splice(toIndex, 0, taskId);
    }

    setSchedule({ ...schedule, [selectedDay]: newDayTasks });
    dragItem.current = null;
    dragOver.current = null;
    dragSource.current = null;
  };

  const handleDropOnAvailable = (e) => {
    e.preventDefault();
    const taskId = dragItem.current;
    if (dragSource.current === 'assigned') {
      setSchedule({ ...schedule, [selectedDay]: dayTasks.filter(id => id !== taskId) });
    }
    dragItem.current = null;
    dragOver.current = null;
    dragSource.current = null;
  };

  const handleSaveSchedule = async () => {
    if (!window.confirm(`Save schedule for ${selectedDay}?`)) return;
    setSaving(true);
    try {
      await saveWeeklySchedule(schedule);
      showMsg('Schedule saved');
    } catch (e) {
      showMsg('Error saving schedule', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ===== PLAN GRID =====
  const gridTasks = (schedule[planDay] || [])
    .map(id => tasks.find(t => t.id === id))
    .filter(Boolean);

  const getPlanId = (day, week, level) => `${DAY_MAP[day]}W${week}${CAT_MAP[level]}`;

  useEffect(() => {
    const existing = {};
    for (const level of LEVELS) {
      const planId = getPlanId(planDay, planWeek, level.value);
      const plan = plans.find(p => p.id === planId);
      const planExists = !!plan;
      gridTasks.forEach(task => {
        if (!existing[task.id]) existing[task.id] = {};
        const t = plan?.tasks?.find(pt => pt.taskId === task.id);
        if (t) {
          existing[task.id][level.value] = {
            reps: t.reps ?? '',
            sets: t.sets ?? '',
            restTime: t.restTime ?? '',
            comment: t.comment ?? '',
            included: true
          };
        } else {
          existing[task.id][level.value] = {
            reps: '', sets: '', restTime: '', comment: '',
            included: planExists ? false : true
          };
        }
      });
    }
    setGridData(existing);
  }, [planDay, planWeek, plans]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGridChange = (taskId, level, field, value) => {
    setGridData(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [level]: { ...(prev[taskId]?.[level] || {}), [field]: value }
      }
    }));
  };

  const handleLevelCheckToggle = (taskId, level) => {
    const current = gridData[taskId]?.[level]?.included ?? true;
    handleGridChange(taskId, level, 'included', !current);
  };

  const isMasterChecked = (taskId) => {
    return LEVELS.every(l => (gridData[taskId]?.[l.value]?.included ?? true));
  };

  const handleMasterCheckToggle = (taskId) => {
    const newVal = !isMasterChecked(taskId);
    setGridData(prev => {
      const updated = { ...prev, [taskId]: { ...prev[taskId] } };
      LEVELS.forEach(l => {
        updated[taskId][l.value] = { ...(updated[taskId][l.value] || {}), included: newVal };
      });
      return updated;
    });
  };

  const buildTaskListForLevel = (level) => {
    return gridTasks
      .filter(task => (gridData[task.id]?.[level.value]?.included ?? true))
      .map(task => {
        if (task.type === 'nutrition') {
          const comment = gridData[task.id]?.[level.value]?.comment || '';
          return comment ? { taskId: task.id, comment } : { taskId: task.id };
        }
        return {
          taskId: task.id,
          reps: parseInt(gridData[task.id]?.[level.value]?.reps) || 0,
          sets: parseInt(gridData[task.id]?.[level.value]?.sets) || 0,
          restTime: parseInt(gridData[task.id]?.[level.value]?.restTime) || 0,
          comment: gridData[task.id]?.[level.value]?.comment || ''
        };
      });
  };

  const findZeroLevels = () => {
    const flagged = [];
    LEVELS.forEach(level => {
      const list = buildTaskListForLevel(level).filter(t => {
        const task = gridTasks.find(gt => gt.id === t.taskId);
        return task && task.type !== 'nutrition';
      });
      if (list.length > 0 && list.every(t => (t.reps || 0) === 0 && (t.sets || 0) === 0)) {
        flagged.push(level.short);
      }
    });
    return flagged;
  };

  const saveGrid = async (targetWeek) => {
    const hasExisting = LEVELS.some(l => plans.find(p => p.id === getPlanId(planDay, targetWeek, l.value)));
    if (hasExisting) {
      if (!window.confirm(`Plans for ${planDay} Week ${targetWeek} already exist. Overwrite?`)) return false;
    }
    const zeroLevels = findZeroLevels();
    if (zeroLevels.length > 0) {
      if (!window.confirm(`Warning: ${zeroLevels.join(', ')} have all reps/sets as 0. Continue saving anyway?`)) {
        return false;
      }
    }
    setSaving(true);
    try {
      for (const level of LEVELS) {
        const taskList = buildTaskListForLevel(level);
        await saveDailyPlan(planDay, targetWeek, level.value, taskList);
      }
      showMsg(`Plans saved for ${planDay} Week ${targetWeek}`);
      loadAll();
      return true;
    } catch (e) {
      showMsg('Error saving plans', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToNextWeek = async () => {
    const saved = await saveGrid(planWeek);
    if (saved !== false) {
      const nextWeek = planWeek + 1;
      const hasExisting = LEVELS.some(l => plans.find(p => p.id === getPlanId(planDay, nextWeek, l.value)));
      if (hasExisting) {
        if (!window.confirm(`Plans for ${planDay} Week ${nextWeek} already exist. Overwrite?`)) return;
      }
      setSaving(true);
      try {
        for (const level of LEVELS) {
          const taskList = buildTaskListForLevel(level);
          await saveDailyPlan(planDay, nextWeek, level.value, taskList);
        }
        showMsg(`Copied to Week ${nextWeek}`);
        setPlanWeek(nextWeek);
        loadAll();
      } catch (e) {
        showMsg('Error copying plans', 'error');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDeletePlanForDayWeek = async () => {
    if (!window.confirm(`Delete ALL levels' plans for ${planDay} Week ${planWeek}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await deleteDailyPlansForDayWeek(planDay, planWeek);
      showMsg(`Deleted plans for ${planDay} Week ${planWeek}`);
      loadAll();
    } catch (e) {
      showMsg('Error deleting plans', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getTrackerStatus = (day, week) => {
    const complete = LEVELS.every(l => plans.some(p => p.id === getPlanId(day, week, l.value)));
    const partial = LEVELS.some(l => plans.some(p => p.id === getPlanId(day, week, l.value)));
    return complete ? 'complete' : partial ? 'partial' : 'empty';
  };

  // ===== CAMP PLANS =====
  useEffect(() => {
    const existing = campPlans.find(c => Number(c.campNumber) === selectedCamp);
    if (existing) {
      const grid = {};
      const orderedIds = [];
      existing.tasks.forEach(t => {
        orderedIds.push(t.taskId);
        grid[t.taskId] = {
          reps: t.reps ?? '',
          sets: t.sets ?? '',
          restTime: t.restTime ?? '',
          comment: t.comment ?? ''
        };
      });
      setCampTaskList(orderedIds);
      setCampGrid(grid);
    } else {
      setCampTaskList([]);
      setCampGrid({});
    }
  }, [selectedCamp, campPlans, tasks]);

  const campAvailableTasks = tasks.filter(t => !campTaskList.includes(t.id));

  const handleCampGridChange = (taskId, field, value) => {
    setCampGrid(prev => ({
      ...prev,
      [taskId]: { ...(prev[taskId] || {}), [field]: value }
    }));
  };

  const handleAddCampTask = (taskId) => {
    if (campTaskList.includes(taskId)) return;
    setCampTaskList(prev => [...prev, taskId]);
    setCampGrid(prev => ({
      ...prev,
      [taskId]: prev[taskId] || { reps:'', sets:'', restTime:'', comment:'' }
    }));
  };

  const handleRemoveCampTask = (taskId) => {
    setCampTaskList(prev => prev.filter(id => id !== taskId));
  };

  const handleCampDragStart = (e, taskId) => {
    campDragItem.current = taskId;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCampDragOverRow = (e, taskId) => {
    e.preventDefault();
    campDragOver.current = taskId;
  };

  const handleCampDrop = (e) => {
    e.preventDefault();
    const taskId = campDragItem.current;
    const overId = campDragOver.current;
    if (taskId && overId && taskId !== overId) {
      setCampTaskList(prev => {
        const list = [...prev];
        const fromIndex = list.indexOf(taskId);
        const toIndex = list.indexOf(overId);
        if (fromIndex === -1 || toIndex === -1) return prev;
        list.splice(fromIndex, 1);
        list.splice(toIndex, 0, taskId);
        return list;
      });
    }
    campDragItem.current = null;
    campDragOver.current = null;
  };

  const handleSaveCampPlan = async () => {
    const existing = campPlans.find(c => Number(c.campNumber) === selectedCamp);
    if (existing) {
      if (!window.confirm(`Camp ${selectedCamp} plan already exists. Overwrite?`)) return;
    }
    if (campTaskList.length === 0) {
      if (!window.confirm(`Camp ${selectedCamp} plan has no tasks. Save an empty plan anyway?`)) return;
    }
    setSaving(true);
    try {
      const taskList = campTaskList
        .map(id => tasks.find(t => t.id === id))
        .filter(Boolean)
        .map(t => {
          if (t.type === 'nutrition') {
            const comment = campGrid[t.id]?.comment || '';
            return comment ? { taskId: t.id, comment } : { taskId: t.id };
          }
          return {
            taskId: t.id,
            reps: parseInt(campGrid[t.id]?.reps) || 0,
            sets: parseInt(campGrid[t.id]?.sets) || 0,
            restTime: parseInt(campGrid[t.id]?.restTime) || 0,
            comment: campGrid[t.id]?.comment || ''
          };
        });
      await saveCampPlan(selectedCamp, taskList);
      showMsg(`Camp ${selectedCamp} plan saved`);
      loadAll();
    } catch (e) {
      showMsg('Error saving camp plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading && tasks.length === 0) return <div className="admin-section-title">लोड होत आहे...</div>;

  return (
    <div>
      <div className="admin-section-title">Plans</div>

      <div style={{ display:'flex', gap:'8px', marginBottom:'24px', flexWrap:'wrap' }}>
        {[
          { id:'tasks', label:'Task Library' },
          { id:'schedule', label:'Weekly Schedule' },
          { id:'grid', label:'Daily Plans' },
          { id:'camps', label:'Camp Plans' },
        ].map(s => (
          <button key={s.id}
            className={`admin-btn ${section === s.id ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setSection(s.id)}>
            {s.label}
          </button>
        ))}
      </div>

      {statusMsg && (
        <div className={`admin-status-msg ${statusMsg.type}`} style={{ marginBottom:'16px' }}>
          {statusMsg.text}
        </div>
      )}

      {/* ===== TASK LIBRARY ===== */}
      {section === 'tasks' && (
        <div>
          <div className="admin-card">
            <h3>Create New Task</h3>
            <form onSubmit={handleCreateTask}>
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Name (English)</label>
                  <input type="text" placeholder="Squats" value={newTask.nameEn}
                    onChange={e => setNewTask({ ...newTask, nameEn: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label>Name (Marathi)</label>
                  <input type="text" placeholder="स्क्वॅट्स" value={newTask.nameMr}
                    onChange={e => setNewTask({ ...newTask, nameMr: e.target.value })} />
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Type</label>
                  <select value={newTask.type}
                    onChange={e => setNewTask({ ...newTask, type: e.target.value })}>
                    <option value="athletics">Athletics</option>
                    <option value="strength">Strength</option>
                    <option value="nutrition">Nutrition</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>MediaPipe Detection</label>
                  <select value={newTask.hasMediaPipe ? newTask.mediaPipeType : ''}
                    onChange={e => setNewTask({ ...newTask, hasMediaPipe: !!e.target.value, mediaPipeType: e.target.value })}>
                    <option value="">None</option>
                    <option value="squats">Squats</option>
                    <option value="jumpingJacks">Jumping Jacks</option>
                    <option value="plank">Plank</option>
                  </select>
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-group" style={{ flex: 1 }}>
                  <label>YouTube URL (optional)</label>
                  <input type="text" placeholder="https://www.youtube.com/watch?v=..."
                    value={newTask.youtubeURL}
                    onChange={e => setNewTask({ ...newTask, youtubeURL: e.target.value })} />
                </div>
              </div>
              <div className="admin-form-row">
                <div className="admin-form-group" style={{ flex: 1 }}>
                  <label>Description (optional)</label>
                  <textarea rows={2} placeholder="Short description shown in Library"
                    value={newTask.description}
                    onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd',
                      borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit',
                      resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
              </div>
              <button type="submit" className="admin-btn admin-btn-primary" disabled={loading}>
                {loading ? 'Creating...' : 'Create Task'}
              </button>
            </form>
          </div>

          <div className="admin-card">
            <h3>All Tasks ({tasks.length})</h3>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name (EN)</th><th>Name (MR)</th><th>Type</th>
                  <th>MediaPipe</th><th>YouTube</th><th>Edit</th><th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {[...tasks].sort((a,b) => {
                  const o = { athletics:1, strength:2, nutrition:3 };
                  return (o[a.type]||9) - (o[b.type]||9);
                }).map(task => (
                  <React.Fragment key={task.id}>
                    <tr>
                      <td>{task.name?.en}</td>
                      <td>{task.name?.mr}</td>
                      <td style={{ color: TYPE_COLORS[task.type], fontWeight:600, fontSize:'13px' }}>
                        {task.type}
                      </td>
                      <td>{task.mediaPipeType || '—'}</td>
                      <td>{task.youtubeURL ? '✓' : '—'}</td>
                      <td>
                        <button className="admin-btn admin-btn-secondary"
                          style={{ padding:'4px 10px', fontSize:'13px' }}
                          onClick={() => {
                            if (editingTaskId === task.id) { setEditingTaskId(null); return; }
                            setEditingTaskId(task.id);
                            setEditTaskForm({
                              nameEn: task.name?.en || '',
                              nameMr: task.name?.mr || '',
                              type: task.type,
                              hasMediaPipe: task.hasMediaPipe || false,
                              mediaPipeType: task.mediaPipeType || '',
                              youtubeURL: task.youtubeURL || '',
                              description: task.description || ''
                            });
                          }}>
                          {editingTaskId === task.id ? 'Cancel' : 'Edit'}
                        </button>
                      </td>
                      <td>
                        <button className="admin-btn admin-btn-danger"
                          style={{ padding:'4px 10px', fontSize:'13px' }}
                          onClick={() => handleDeleteTask(task)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                    {editingTaskId === task.id && (
                      <tr>
                        <td colSpan={7} style={{ background:'#f8f9fa', padding:'16px' }}>
                          <div className="admin-form-row">
                            <div className="admin-form-group">
                              <label>Name (English)</label>
                              <input type="text" value={editTaskForm.nameEn}
                                onChange={e => setEditTaskForm({ ...editTaskForm, nameEn: e.target.value })} />
                            </div>
                            <div className="admin-form-group">
                              <label>Name (Marathi)</label>
                              <input type="text" value={editTaskForm.nameMr}
                                onChange={e => setEditTaskForm({ ...editTaskForm, nameMr: e.target.value })} />
                            </div>
                            <div className="admin-form-group">
                              <label>Type</label>
                              <select value={editTaskForm.type}
                                onChange={e => setEditTaskForm({ ...editTaskForm, type: e.target.value })}>
                                <option value="athletics">Athletics</option>
                                <option value="strength">Strength</option>
                                <option value="nutrition">Nutrition</option>
                              </select>
                            </div>
                            <div className="admin-form-group">
                              <label>MediaPipe</label>
                              <select value={editTaskForm.hasMediaPipe ? editTaskForm.mediaPipeType : ''}
                                onChange={e => setEditTaskForm({ ...editTaskForm, hasMediaPipe: !!e.target.value, mediaPipeType: e.target.value })}>
                                <option value="">None</option>
                                <option value="squats">Squats</option>
                                <option value="jumpingJacks">Jumping Jacks</option>
                                <option value="plank">Plank</option>
                              </select>
                            </div>
                          </div>
                          <div className="admin-form-row">
                            <div className="admin-form-group" style={{ flex: 1 }}>
                              <label>YouTube URL (optional)</label>
                              <input type="text" placeholder="https://www.youtube.com/watch?v=..."
                                value={editTaskForm.youtubeURL}
                                onChange={e => setEditTaskForm({ ...editTaskForm, youtubeURL: e.target.value })} />
                            </div>
                          </div>
                          <div className="admin-form-row">
                            <div className="admin-form-group" style={{ flex: 1 }}>
                              <label>Description (optional)</label>
                              <textarea rows={2}
                                value={editTaskForm.description}
                                onChange={e => setEditTaskForm({ ...editTaskForm, description: e.target.value })}
                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd',
                                  borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit',
                                  resize: 'vertical', boxSizing: 'border-box' }} />
                            </div>
                          </div>
                          <button className="admin-btn admin-btn-primary"
                            onClick={() => handleEditTask(task.id)}>
                            Save Changes
                          </button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== WEEKLY SCHEDULE ===== */}
      {section === 'schedule' && (
        <div className="admin-card">
          <h3>Weekly Schedule</h3>
          <div className="admin-form-row" style={{ marginBottom:'20px' }}>
            <div className="admin-form-group">
              <label>Day</label>
              <select value={selectedDay} onChange={e => setSelectedDay(e.target.value)}>
                {DAYS.map(d => (
                  <option key={d} value={d}>
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                    {(schedule[d] || []).length > 0 ? ` (${(schedule[d] || []).length} tasks)` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'24px' }}>
            <div>
              <h4 style={{ marginBottom:'12px', color:'#1a1a2e' }}>
                {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)} — {dayTasks.length} tasks
              </h4>
              <div
                onDragOver={e => e.preventDefault()}
                onDrop={handleDropOnAssigned}
                style={{ minHeight:'200px', background:'#f8f9fa', borderRadius:'8px', padding:'8px', border:'2px dashed #e9ecef' }}>
                {dayTasks.length === 0 && (
                  <p style={{ color:'#aaa', fontSize:'14px', textAlign:'center', marginTop:'60px' }}>
                    Drag tasks here
                  </p>
                )}
                {dayTasks.map((taskId, i) => {
                  const task = tasks.find(t => t.id === taskId);
                  if (!task) return null;
                  return (
                    <div key={taskId}
                      draggable
                      onDragStart={e => handleDragStart(e, taskId, 'assigned')}
                      onDragOver={e => handleDragOverAssigned(e, taskId)}
                      style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px',
                        background:'white', padding:'8px 12px', borderRadius:'8px',
                        border:'1px solid #e9ecef', cursor:'grab' }}>
                      <span style={{ color:'#aaa', fontSize:'12px', minWidth:'20px' }}>{i+1}</span>
                      <span style={{ flex:1, fontSize:'14px', fontWeight:500 }}>{task.name?.en}</span>
                      <span style={{ fontSize:'11px', color: TYPE_COLORS[task.type], fontWeight:600 }}>
                        {task.type}
                      </span>
                      <span style={{ color:'#aaa', cursor:'grab' }}>⠿</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 style={{ marginBottom:'12px', color:'#1a1a2e' }}>Available Tasks</h4>
              <div
                onDragOver={e => e.preventDefault()}
                onDrop={handleDropOnAvailable}
                style={{ minHeight:'200px', background:'#f0f8ff', borderRadius:'8px', padding:'8px', border:'2px dashed #2563eb33' }}>
                {availableTasks.length === 0 && (
                  <p style={{ color:'#aaa', fontSize:'14px', textAlign:'center', marginTop:'60px' }}>
                    All tasks added
                  </p>
                )}
                {availableTasks.map(task => (
                  <div key={task.id}
                    draggable
                    onDragStart={e => handleDragStart(e, task.id, 'available')}
                    style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px',
                      background:'white', padding:'8px 12px', borderRadius:'8px',
                      border:'1px solid #e9ecef', cursor:'grab' }}>
                    <span style={{ flex:1, fontSize:'14px' }}>{task.name?.en}</span>
                    <span style={{ fontSize:'11px', color: TYPE_COLORS[task.type], fontWeight:600 }}>
                      {task.type}
                    </span>
                    <span style={{ color:'#aaa' }}>⠿</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button className="admin-btn admin-btn-primary" style={{ marginTop:'16px' }}
            onClick={handleSaveSchedule} disabled={saving}>
            {saving ? 'Saving...' : `Save ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)} Schedule`}
          </button>
        </div>
      )}

      {/* ===== DAILY PLANS GRID ===== */}
      {section === 'grid' && (
        <div>
          <div className="admin-card">
            <div style={{ display:'flex', gap:'16px', flexWrap:'wrap', marginBottom:'20px', alignItems:'flex-end' }}>
              <div className="admin-form-group">
                <label>Day</label>
                <select value={planDay} onChange={e => setPlanDay(e.target.value)}>
                  {DAYS.map(d => (
                    <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="admin-form-group">
                <label>Week</label>
                <select value={planWeek} onChange={e => setPlanWeek(parseInt(e.target.value))}>
                  {Array.from({length:20},(_,i)=>i+1).map(w => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select>
              </div>
              <button className="admin-btn admin-btn-primary"
                onClick={() => saveGrid(planWeek)} disabled={saving}>
                {saving ? 'Saving...' : 'Save All'}
              </button>
              <button className="admin-btn admin-btn-secondary"
                onClick={handleCopyToNextWeek} disabled={saving}>
                Copy → Week {planWeek + 1}
              </button>
              <button className="admin-btn admin-btn-danger"
                onClick={handleDeletePlanForDayWeek} disabled={saving}>
                Delete Plan (this Day+Week)
              </button>
            </div>

            {gridTasks.length === 0 ? (
              <p style={{ color:'#999' }}>
                No tasks in schedule for {planDay}. Set up Weekly Schedule first.
              </p>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth:'160px' }}>Task</th>
                      {LEVELS.map(l => (
                        <th key={l.value}
                          style={{ textAlign:'center', borderLeft:'2px solid #e9ecef', background:'#f0f4ff', minWidth:'210px' }}>
                          {l.short}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {gridTasks.map(task => (
                      <tr key={task.id}>
                        <td>
                          <div style={{ display:'flex', alignItems:'flex-start', gap:'8px' }}>
                            <input type="checkbox"
                              checked={isMasterChecked(task.id)}
                              onChange={() => handleMasterCheckToggle(task.id)}
                              style={{ marginTop:'3px' }} />
                            <div>
                              <div style={{ fontWeight:600, fontSize:'13px' }}>{task.name?.en}</div>
                              <div style={{ fontSize:'11px', color: TYPE_COLORS[task.type] }}>{task.type}</div>
                            </div>
                          </div>
                        </td>
                        {LEVELS.map(l => {
                          const cell = gridData[task.id]?.[l.value] || {};
                          const included = cell.included ?? true;
                          return (
                            <td key={l.value} style={{ borderLeft:'2px solid #e9ecef', padding:'6px', verticalAlign:'top', opacity: included ? 1 : 0.4 }}>
                              {task.type !== 'nutrition' && (
                                <div style={{ display:'flex', gap:'4px', marginBottom:'6px' }}>
                                  <input type="number" min="0" placeholder="Reps" disabled={!included}
                                    value={cell.reps ?? ''}
                                    onChange={e => handleGridChange(task.id, l.value, 'reps', e.target.value)}
                                    style={{ width:'52px', padding:'4px', border:'1px solid #ddd',
                                      borderRadius:'4px', fontSize:'13px', textAlign:'center' }} />
                                  <input type="number" min="0" placeholder="Sets" disabled={!included}
                                    value={cell.sets ?? ''}
                                    onChange={e => handleGridChange(task.id, l.value, 'sets', e.target.value)}
                                    style={{ width:'52px', padding:'4px', border:'1px solid #ddd',
                                      borderRadius:'4px', fontSize:'13px', textAlign:'center' }} />
                                  <input type="number" min="0" placeholder="Rest" disabled={!included}
                                    value={cell.restTime ?? ''}
                                    onChange={e => handleGridChange(task.id, l.value, 'restTime', e.target.value)}
                                    style={{ width:'52px', padding:'4px', border:'1px solid #ddd',
                                      borderRadius:'4px', fontSize:'13px', textAlign:'center' }} />
                                </div>
                              )}
                              <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                                <input type="checkbox" checked={included}
                                  onChange={() => handleLevelCheckToggle(task.id, l.value)} />
                                <input type="text" placeholder="Comment"
                                  disabled={!included}
                                  value={cell.comment ?? ''}
                                  onChange={e => handleGridChange(task.id, l.value, 'comment', e.target.value)}
                                  style={{ flex:1, minWidth:'110px', padding:'4px 6px', border:'1px solid #ddd',
                                    borderRadius:'4px', fontSize:'12px' }} />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="admin-card">
            <h3>Plan Completion Tracker</h3>
            <p style={{ fontSize:'13px', color:'#888', marginBottom:'12px' }}>
              Click any cell to jump to that day + week.
            </p>
            <div style={{ overflowX:'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Week</th>
                    {DAYS.map(d => <th key={d} style={{ textAlign:'center' }}>{DAY_MAP[d]}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({length:20},(_,i)=>i+1).map(w => (
                    <tr key={w}>
                      <td style={{ fontWeight:600 }}>W{w}</td>
                      {DAYS.map(d => {
                        const s = getTrackerStatus(d, w);
                        return (
                          <td key={d} style={{ textAlign:'center', cursor:'pointer', padding:'8px' }}
                            onClick={() => { setPlanDay(d); setPlanWeek(w); }}>
                            <span style={{
                              display:'inline-block', width:'18px', height:'18px', borderRadius:'50%',
                              background: s==='complete' ? '#28a745' : s==='partial' ? '#fd7e14' : '#e9ecef'
                            }} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display:'flex', gap:'16px', marginTop:'12px', fontSize:'13px' }}>
              {[['#28a745','Complete'],['#fd7e14','Partial'],['#e9ecef','Empty']].map(([color, label]) => (
                <span key={label}>
                  <span style={{ display:'inline-block', width:'12px', height:'12px',
                    borderRadius:'50%', background:color, marginRight:'4px' }} />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== CAMP PLANS ===== */}
      {section === 'camps' && (
        <div className="admin-card">
          <h3>Camp Plans</h3>
          <div className="admin-form-row" style={{ marginBottom:'20px' }}>
            <div className="admin-form-group">
              <label>Camp</label>
              <select value={selectedCamp} onChange={e => setSelectedCamp(parseInt(e.target.value))}>
                {[1,2,3,4,5].map(n => (
                  <option key={n} value={n}>
                    Camp {n} {campPlans.find(c => Number(c.campNumber) === n) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Camp tasks (ordered) */}
          <h4 style={{ marginBottom:'12px', color:'#1a1a2e' }}>
            Camp {selectedCamp} Tasks — {campTaskList.length} tasks
          </h4>
          {campTaskList.length === 0 ? (
            <p style={{ color:'#aaa', fontSize:'14px', background:'#f8f9fa', borderRadius:'8px',
              padding:'24px', textAlign:'center', border:'2px dashed #e9ecef' }}>
              No tasks yet. Add tasks from the list below.
            </p>
          ) : (
            <div style={{ overflowX:'auto' }} onDrop={handleCampDrop} onDragOver={e => e.preventDefault()}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width:'30px' }}></th>
                    <th style={{ width:'30px' }}>#</th>
                    <th>Task</th><th>Type</th>
                    <th>Reps</th><th>Sets</th><th>Rest (sec)</th>
                    <th style={{ minWidth:'160px' }}>Comment</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {campTaskList.map((taskId, i) => {
                    const task = tasks.find(t => t.id === taskId);
                    if (!task) return null;
                    return (
                      <tr key={taskId} onDragOver={e => handleCampDragOverRow(e, taskId)}>
                        <td>
                          <span
                            draggable
                            onDragStart={e => handleCampDragStart(e, taskId)}
                            style={{ color:'#aaa', cursor:'grab', fontSize:'16px', userSelect:'none' }}
                            title="Drag to reorder">
                            ⠿
                          </span>
                        </td>
                        <td style={{ color:'#aaa', fontSize:'12px' }}>{i + 1}</td>
                        <td style={{ fontWeight:500 }}>{task.name?.en}</td>
                        <td style={{ fontSize:'13px', color: TYPE_COLORS[task.type] }}>{task.type}</td>
                        {task.type === 'nutrition' ? (
                          <td colSpan={3} style={{ color:'#999', fontSize:'12px' }}>—</td>
                        ) : (
                          ['reps','sets','restTime'].map(field => (
                            <td key={field}>
                              <input type="number" min="0"
                                value={campGrid[taskId]?.[field] ?? ''}
                                onChange={e => handleCampGridChange(taskId, field, e.target.value)}
                                style={{ width:'60px', padding:'4px', border:'1px solid #ddd', borderRadius:'4px' }} />
                            </td>
                          ))
                        )}
                        <td>
                          <input type="text" placeholder="Comment"
                            value={campGrid[taskId]?.comment ?? ''}
                            onChange={e => handleCampGridChange(taskId, 'comment', e.target.value)}
                            style={{ width:'100%', minWidth:'150px', padding:'4px 6px',
                              border:'1px solid #ddd', borderRadius:'4px', fontSize:'12px' }} />
                        </td>
                        <td>
                          <button className="admin-btn admin-btn-danger"
                            style={{ padding:'4px 10px', fontSize:'13px' }}
                            onClick={() => handleRemoveCampTask(taskId)}>
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Available tasks */}
          <h4 style={{ margin:'20px 0 12px', color:'#1a1a2e' }}>Available Tasks</h4>
          <div style={{ background:'#f0f8ff', borderRadius:'8px', padding:'8px', border:'2px dashed #2563eb33' }}>
            {campAvailableTasks.length === 0 ? (
              <p style={{ color:'#aaa', fontSize:'14px', textAlign:'center', padding:'16px' }}>
                All tasks added
              </p>
            ) : (
              campAvailableTasks.map(task => (
                <div key={task.id}
                  style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px',
                    background:'white', padding:'8px 12px', borderRadius:'8px',
                    border:'1px solid #e9ecef' }}>
                  <span style={{ flex:1, fontSize:'14px' }}>{task.name?.en}</span>
                  <span style={{ fontSize:'11px', color: TYPE_COLORS[task.type], fontWeight:600 }}>
                    {task.type}
                  </span>
                  <button className="admin-btn admin-btn-secondary"
                    style={{ padding:'4px 12px', fontSize:'13px' }}
                    onClick={() => handleAddCampTask(task.id)}>
                    + Add
                  </button>
                </div>
              ))
            )}
          </div>

          <button className="admin-btn admin-btn-primary" style={{ marginTop:'16px' }}
            onClick={handleSaveCampPlan} disabled={saving}>
            {saving ? 'Saving...' : `Save Camp ${selectedCamp} Plan`}
          </button>
        </div>
      )}
    </div>
  );
}